import { __assign, __read, __rest, __spreadArray } from "tslib";
import { SmEvent } from './SmEvent';
import { Logger } from './utils/Logger';
import { SpeechMarkerName } from './websocket-message/scene/response-body/SpeechMarker';
import { ConversationState } from './ConversationState';
/**
 * Stores and content cards
 *
 * @public
 */
var Conversation = /** @class */ (function () {
    function Conversation(logger, conversationState) {
        if (logger === void 0) { logger = new Logger(); }
        if (conversationState === void 0) { conversationState = new ConversationState(); }
        this.logger = logger;
        this.conversationState = conversationState;
        this._onCardChanged = new SmEvent();
        this._autoClearCards = false;
        this.cardData = new Map();
        this.activeCardIds = new Set();
    }
    Conversation.prototype.processStateMessage = function (message) {
        this.conversationState.processStateMessage(message);
    };
    Conversation.prototype.processRecognizeResultsMessage = function (message) {
        this.conversationState.processRecognizeResultsMessage(message);
    };
    Object.defineProperty(Conversation.prototype, "onConversationStateUpdated", {
        /**
         * A callback function which fires when conversation state changes
         */
        get: function () {
            return this.conversationState.onConversationStateUpdated;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Conversation.prototype, "autoClearCards", {
        /**
         * Automatically clear active content cards each conversation turn
         */
        set: function (enabled) {
            this._autoClearCards = enabled;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Conversation.prototype, "onCardChanged", {
        /**
         * A callback function which fires when active cards are changed
         */
        get: function () {
            return this._onCardChanged;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Conversation.prototype, "activeCards", {
        get: function () {
            var _this = this;
            var activeCardInfo = [];
            this.activeCardIds.forEach(function (id) {
                var data = _this.cardData.get(id);
                if (data) {
                    activeCardInfo.push(__assign(__assign({}, data), { id: id }));
                }
                else {
                    _this.logger.log('error', "card data for ".concat(id, " does not exist"));
                }
            });
            return activeCardInfo;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Handles speech marker messages and updates the active card state
     */
    Conversation.prototype.onSpeechMarker = function (messageBody) {
        var cardIds = messageBody.arguments;
        if (messageBody.name === SpeechMarkerName.Showcards) {
            this.addActiveCardIds(cardIds);
        }
        else if (messageBody.name === SpeechMarkerName.Hidecards) {
            this.removeActiveCards(cardIds);
        }
        if (
        // Hide all cards when arg list is empty and hidecards message is received
        cardIds.length === 0 &&
            messageBody.name === SpeechMarkerName.Hidecards) {
            this.clearActiveCards();
        }
    };
    /**
     * Stores content card data contained in conversation result messages
     */
    Conversation.prototype.onConversationResult = function (messageBody) {
        var _this = this;
        var cards = this.formatMessageBody(messageBody);
        if (this._autoClearCards) {
            this.clearActiveCards();
        }
        cards.map(function (_a) {
            var id = _a.id, data = _a.data;
            _this.cardData.set(id, data);
        });
    };
    /**
     * Clears active card ids and data. Emits a card changed event
     */
    Conversation.prototype.reset = function () {
        this.clearActiveCards();
        this.cardData.clear();
        this.conversationState.reset();
    };
    /**
     * Clears active card ids. Emits a card changed event
     */
    Conversation.prototype.clearActiveCards = function () {
        this.activeCardIds.clear();
        this.onCardChanged.call(this.activeCards);
    };
    Conversation.prototype.formatMessageBody = function (body) {
        var _a;
        var fullfillments = (_a = body.provider.meta.dialogflow) === null || _a === void 0 ? void 0 : _a.queryResult.fulfillmentMessages;
        var data = fullfillments === null || fullfillments === void 0 ? void 0 : fullfillments.find(function (fullfillment) {
            return Boolean(fullfillment === null || fullfillment === void 0 ? void 0 : fullfillment.payload);
        });
        if (data) {
            return this.formatDialogflow(data);
        }
        return this.formatContextData(body.output.context);
    };
    Conversation.prototype.formatDialogflow = function (data) {
        var _this = this;
        var payload = data.payload.soulmachines;
        return Object.keys(payload).map(function (id) {
            return {
                id: id,
                data: _this.formatCardData(payload[id]),
            };
        });
    };
    Conversation.prototype.formatContextData = function (data) {
        var _this = this;
        return this.allowedIds(data).map(function (id) {
            var cardId = id.replace('public-', '');
            return {
                id: cardId,
                data: _this.formatCardData(data[id]),
            };
        });
    };
    Conversation.prototype.allowedIds = function (values) {
        return Object.keys(values)
            .filter(function (k) { return /public-/.test(k) === true; })
            .filter(function (k) { return /\.original/gm.test(k) === false; });
    };
    Conversation.prototype.addActiveCardIds = function (cards) {
        this.activeCardIds = new Set(__spreadArray(__spreadArray([], __read(this.activeCardIds), false), __read(cards), false));
        this.onCardChanged.call(this.activeCards);
    };
    Conversation.prototype.removeActiveCards = function (cards) {
        var _this = this;
        cards.forEach(function (c) { return _this.activeCardIds.delete(c); });
        this.onCardChanged.call(this.activeCards);
    };
    Conversation.prototype.formatCardData = function (data) {
        // Data is could be JSON or an object
        var parsedData = typeof data === 'string' ? JSON.parse(data) : data;
        var component = parsedData.component, type = parsedData.type, rest = __rest(parsedData, ["component", "type"]);
        return __assign({ type: type ? type : component }, rest);
    };
    return Conversation;
}());
export { Conversation };
//# sourceMappingURL=Conversation.js.map